import React from 'react';
import { CalendarX, Edit, Bell, CheckCircle2, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { th } from 'date-fns/locale';
import type { Notification } from '@/hooks/useNotifications';

interface Props {
  notification: Notification;
  onClick: (notification: Notification) => void;
}

const typeIcons: Record<string, React.ReactNode> = {
  SCHEDULE_REQUEST_RESULT: <CalendarX className="h-5 w-5 text-blue-500" />,
  SALARY_CONFIRM: <Bell className="h-5 w-5 text-green-500" />,
  NEWS: <Bell className="h-5 w-5 text-purple-500" />,
};

function getStatusIcon(metadata: Record<string, unknown>) {
  if (metadata?.status === 'APPROVED') return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  if (metadata?.status === 'REJECTED') return <XCircle className="h-4 w-4 text-red-500" />;
  return null;
}

const NotificationItem: React.FC<Props> = ({ notification, onClick }) => {
  const icon = typeIcons[notification.type] || <Bell className="h-5 w-5 text-muted-foreground" />;
  const statusIcon = getStatusIcon(notification.metadata);
  const timeAgo = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: th });

  return (
    <button
      onClick={() => onClick(notification)}
      className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-accent/50 transition-colors ${
        !notification.is_read ? 'bg-accent/20' : ''
      }`}
    >
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-sm ${!notification.is_read ? 'font-semibold' : 'font-medium'}`}>
            {notification.title}
          </span>
          {statusIcon}
        </div>
        {notification.body && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{notification.body}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">{timeAgo}</p>
      </div>
      {!notification.is_read && (
        <div className="mt-2 shrink-0 h-2 w-2 rounded-full bg-blue-500" />
      )}
    </button>
  );
};

export default NotificationItem;
